<template>
  <v-dialog v-model="showDialog" max-width="500">
    <v-card>
      <v-card-title class="text-h5">
        <v-icon color="error" class="mr-2">mdi-delete</v-icon>
        Delete Group
      </v-card-title>
      <v-card-text>
        <v-alert type="warning" variant="tonal" class="mb-4">
          <v-alert-title>This action cannot be undone!</v-alert-title>
        </v-alert>
        
        Are you sure you want to delete the group <strong>"{{ groupData?.name }}"</strong>?
        <br><br>
        This will permanently remove:
        <ul class="mt-2">
          <li>The group and its settings</li>
          <li>All {{ itemCount }} items in this group</li>
        </ul>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="cancelDelete" :disabled="isDeleting">Cancel</v-btn>
        <v-btn 
          color="error" 
          variant="elevated"
          @click="confirmDelete"
          prepend-icon="mdi-delete"
          :loading="isDeleting"
        >
          Delete Group
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, computed } from 'vue'
import { useGroupStore } from '../stores/group'
import { useListStore } from '../stores/list'

export default {
  name: 'DeleteGroupDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    groupData: {
      type: Object,
      default: null
    }
  },
  emits: ['update:modelValue', 'deleted', 'message'],
  setup(props, { emit }) {
    const groupStore = useGroupStore()
    const listStore = useListStore()
    
    const isDeleting = ref(false)
    
    const showDialog = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value)
    })
    
    const itemCount = computed(() => {
      if (!props.groupData || !listStore.currentList?.items) return 0
      return listStore.currentList.items.filter(item => item.group_id === props.groupData.id).length
    })
    
    const showMessage = (message, color = 'success') => {
      emit('message', { message, color })
    }
    
    const confirmDelete = async () => {
      if (!props.groupData) return
      
      isDeleting.value = true
      
      try {
        const result = await groupStore.deleteGroup(props.groupData.id)
        if (result.success) {
          showMessage('Group and all its items deleted successfully!')
          emit('deleted')
          cancelDelete()
          
          // Refresh current list to show updated items
          if (listStore.currentList) {
            await listStore.setCurrentList(listStore.currentList)
          }
        } else {
          showMessage(result.error, 'error')
        }
      } catch (error) {
        showMessage('Failed to delete group', 'error')
      } finally {
        isDeleting.value = false
      }
    }
    
    const cancelDelete = () => {
      showDialog.value = false
    }
    
    return {
      showDialog,
      isDeleting,
      itemCount,
      confirmDelete,
      cancelDelete
    }
  }
}
</script>